import { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as api from '../../api';
import Spinner from '../Atoms/Spinner';

const PageContainer = styled.div`
    max-width: 600px;
    margin: 0 auto;
    padding: 40px 20px;
    font-family: Arial, sans-serif;
`;

const Banner = styled.img`
    display: block;
    margin: 0 auto 30px;
    max-width: 100%;
    border-radius: 8px;
`;

const Title = styled.h1`
    font-size: 24px;
    font-weight: 700;
    text-align: center;
    text-transform: none;
    color: #0000FF;
    margin-bottom: 8px;
`;

const Subtitle = styled.h2`
    font-size: 18px;
    font-weight: 400;
    text-align: center;
    color: #cc0000;
    margin-bottom: 30px;
`;

const EventDetails = styled.div`
    text-align: center;
    margin-bottom: 30px;
    line-height: 1.6;
    color: #333;
    font-size: 15px;

    strong {
        display: block;
        font-size: 16px;
        margin-bottom: 4px;
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Label = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: #333;
`;

const Input = styled.input`
    width: 100%;
    padding: 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 15px;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: #0000FF;
    }
`;

const SubmitButton = styled.button`
    background-color: #0000FF;
    color: white;
    border: none;
    padding: 14px 24px;
    font-size: 15px;
    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:hover {
        background-color: #0000cc;
    }

    &:disabled {
        background-color: #999;
        cursor: not-allowed;
    }
`;

const ImportantNote = styled.div`
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 4px;
    padding: 14px 16px;
    margin-top: 24px;
    font-size: 14px;
    font-weight: 600;
    color: #856404;
    line-height: 1.5;
`;

const Note = styled.p`
    font-size: 13px;
    color: #666;
    margin-top: 12px;
    line-height: 1.5;
`;

const VIANote = styled.p`
    font-size: 13px;
    color: #666;
    margin-top: 20px;
    line-height: 1.5;
    text-align: center;
    font-style: italic;
`;

const SuccessMessage = styled.div`
    text-align: center;
    padding: 40px 20px;

    h2 {
        color: #28a745;
        margin-bottom: 16px;
    }

    p {
        color: #333;
        line-height: 1.6;
    }
`;

const ErrorMessage = styled.p`
    color: #dc3545;
    font-size: 14px;
    margin: 0;
`;

const RequiredStar = styled.span`
    color: #dc3545;
    margin-left: 2px;
`;

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const FooterLinks = styled.div`
    text-align: center;
    margin-top: 30px;
    font-size: 12px;
    color: #999;

    a {
        color: #999;
        text-decoration: underline;
    }
`;

const RSVPTalkFD100 = () => {
    const { i18n } = useTranslation('common');
    const isFr = i18n.language === "fr";
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [company, setCompany] = useState('');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!firstName.trim() || !lastName.trim() || !company.trim() || !email.trim()) {
            setError(isFr ? 'Veuillez remplir tous les champs.' : 'Please fill in all fields.');
            return;
        }

        setLoading(true);
        try {
            const res = await api.submitRSVPTalk({
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                company: company.trim(),
                email: email.trim().toLowerCase()
            });
            if (res.data.message === 'success') {
                setSubmitted(true);
            }
        } catch (err) {
            console.error(err);
            setError(isFr ? 'Une erreur est survenue. Veuillez réessayer.' : 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <PageContainer>
                <Banner src="/img/fd100-table-ronde-banner.png" alt="Table Ronde - Collectionneurs d'art de vivre" />
                <SuccessMessage>
                    <h2>{isFr ? "Merci pour votre inscription !" : "Thank you for your registration!"}</h2>
                    <p>
                        {isFr ? "Votre RSVP a bien été enregistré." : "Your RSVP has been successfully registered."}<br />
                        {isFr
                            ? "Vous recevrez un QR code individuel et non cessible à l'adresse mail renseignée pour accéder au salon ART PARIS le 10 avril à 14h30 précises pour la table ronde \"COLLECTIONNEURS D'ART DE VIVRE\". Ce QR code vous sera envoyé 3 jours avant la Table Ronde."
                            : "You will receive an individual, non-transferable QR code at the email address provided, allowing you to access the ART PARIS fair on April 10 at exactly 2:30 p.m. for the round table \"COLLECTIONNEURS D'ART DE VIVRE\". This QR code will be sent to you three days before the round table."}
                    </p>
                </SuccessMessage>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Banner src="/img/fd100-table-ronde-banner.png" alt="Table Ronde - Collectionneurs d'art de vivre" />
            <Title>{isFr ? "Table ronde" : "Round Table"}</Title>
            <Subtitle>{isFr ? "Formulaire pour obtenir une invitation" : "Invitation Request Form"}</Subtitle>

            <EventDetails>
                <strong>{isFr ? "Vendredi 10 Avril 2026 à 14h30 précises" : "Friday, April 10, 2026 at 2:30 p.m."}</strong>
                {isFr ? "au Grand Palais — Sur le salon ART PARIS" : "At the Grand Palais — within the ART PARIS fair"}<br />
                {isFr ? "Stand B0" : "Stand B0"}<br />
                7 Avenue Winston Churchill, 75008 Paris
            </EventDetails>

            <Form onSubmit={handleSubmit}>
                <FieldGroup>
                    <Label htmlFor="firstName">{isFr ? "Prénom" : "First Name"}<RequiredStar>*</RequiredStar></Label>
                    <Input
                        id="firstName"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                    />
                </FieldGroup>

                <FieldGroup>
                    <Label htmlFor="lastName">{isFr ? "Nom" : "Name"}<RequiredStar>*</RequiredStar></Label>
                    <Input
                        id="lastName"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                    />
                </FieldGroup>

                <FieldGroup>
                    <Label htmlFor="company">{isFr ? "Société" : "Company"}<RequiredStar>*</RequiredStar></Label>
                    <Input
                        id="company"
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        required
                    />
                </FieldGroup>

                <FieldGroup>
                    <Label htmlFor="email">Email<RequiredStar>*</RequiredStar></Label>
                    <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </FieldGroup>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                <SubmitButton type="submit" disabled={loading}>
                    {loading && <Spinner />}
                    {isFr
                        ? "Je veux participer et recevoir un QR code personnalisé pour accéder à la table ronde lors du salon ART PARIS"
                        : "I would like to participate and receive a personalized QR code to access the round table at the ART PARIS fair"}
                </SubmitButton>
            </Form>

            <ImportantNote>
                {isFr
                    ? "Invitation individuelle, personnelle et non cessible. Les places étant limitées, nous vous recommandons de répondre rapidement."
                    : "Invitation for one person only. This invitation is personal and non-transferable. As seating is limited, we recommend that you reply promptly."}
            </ImportantNote>
            <Note>
                {isFr
                    ? "Vous recevrez un QR code individuel et non cessible à l'adresse mail renseignée pour accéder au salon ART PARIS le 10 avril à 14h30 précises pour la table ronde \"COLLECTIONNEURS D'ART DE VIVRE\". Ce QR code vous sera envoyé 3 jours avant la Table Ronde."
                    : "You will receive an individual, non-transferable QR code at the email address provided, allowing you to access the ART PARIS fair on April 10 at exactly 2:30 p.m. for the round table \"COLLECTIONNEURS D'ART DE VIVRE\". This QR code will be sent to you three days before the round table."}
            </Note>
            <VIANote>
                {isFr
                    ? "Depuis plus de 40 ans, le VIA insuffle l'innovation française dans les espaces de vie. Son projet associatif, et la diversité de ses membres (designers, architectes d'intérieur, fabricants, éditeurs et prescripteurs), favorisent écoute, agilité, créativité et rayonnement, sous le contrôle mixte des ministères de la Culture et de l'Économie."
                    : "For over 40 years, VIA has been driving French innovation in living spaces. Its non-profit mission, and the diversity of its members (designers, interior architects, manufacturers, publishers and specifiers), foster openness, agility, creativity and influence, under the joint oversight of the Ministries of Culture and Economy."}
            </VIANote>
            <FooterLinks>
                <a href="https://lefrenchdesign.org/mentions-legales">{isFr ? "Mentions légales" : "Legal notice"}</a> — <a href="https://lefrenchdesign.org">{isFr ? "Site web" : "Website"}</a>
            </FooterLinks>
        </PageContainer>
    );
};

export default RSVPTalkFD100;
